'use client'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'

const scrollButtonClass =
  'flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:border-white/70 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-default disabled:opacity-25 disabled:hover:border-white/30 disabled:hover:bg-transparent motion-reduce:transition-none lg:hidden'

interface PerformerStripScrollerProps {
  children: ReactNode
  idPrefix: string
  label: string
  backgroundColor: string
}

export function PerformerStripScroller({
  children,
  idPrefix,
  label,
  backgroundColor,
}: PerformerStripScrollerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollBack, setCanScrollBack] = useState(false)
  const [canScrollForward, setCanScrollForward] = useState(false)
  const isScrollable = canScrollBack || canScrollForward

  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return

    const updateScrollState = () => {
      setCanScrollBack(scroller.scrollLeft > 1)
      setCanScrollForward(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
      )
    }

    const observer = new ResizeObserver(updateScrollState)
    observer.observe(scroller)
    if (scroller.firstElementChild) {
      observer.observe(scroller.firstElementChild)
    }
    scroller.addEventListener('scroll', updateScrollState, { passive: true })

    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', updateScrollState)
    }
  }, [])

  const scroll = (direction: number) => {
    const scroller = scrollRef.current
    if (!scroller) return

    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.8,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    })
  }

  return (
    <>
      <div
        className="flex items-center gap-2 sm:gap-3"
        style={{ '--performer-strip-bg': backgroundColor } as CSSProperties}
      >
        <button
          type="button"
          aria-label="Show previous performers"
          aria-controls={`${idPrefix}-performer-strip`}
          disabled={!canScrollBack}
          onClick={() => scroll(-1)}
          className={`${scrollButtonClass} ${isScrollable ? '' : 'invisible'}`}
        >
          <ChevronLeftIcon aria-hidden="true" className="size-5" />
        </button>

        <div className="relative min-w-0 flex-1">
          <div
            ref={scrollRef}
            id={`${idPrefix}-performer-strip`}
            role="region"
            aria-label={label}
            aria-describedby={
              isScrollable ? `${idPrefix}-performer-scroll-hint` : undefined
            }
            tabIndex={isScrollable ? 0 : undefined}
            className="overflow-x-auto overscroll-x-contain rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {children}
          </div>
          {canScrollBack && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-linear-to-r from-(--performer-strip-bg) to-transparent lg:hidden"
            />
          )}
          {canScrollForward && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-linear-to-l from-(--performer-strip-bg) to-transparent lg:hidden"
            />
          )}
        </div>

        <button
          type="button"
          aria-label="Show more performers"
          aria-controls={`${idPrefix}-performer-strip`}
          disabled={!canScrollForward}
          onClick={() => scroll(1)}
          className={`${scrollButtonClass} ${isScrollable ? '' : 'invisible'}`}
        >
          <ChevronRightIcon aria-hidden="true" className="size-5" />
        </button>
      </div>
      {isScrollable && (
        <p
          id={`${idPrefix}-performer-scroll-hint`}
          className="mt-2 text-center text-xs text-white/65 lg:hidden"
        >
          Swipe or scroll to explore the lineup
        </p>
      )}
    </>
  )
}
